import React, { FunctionComponent, useState } from "react";
import Modal, { Styles } from "react-modal";
import { connect, useDispatch } from "react-redux";
import Loading from "../../Loading/Loading";
import { createNews } from "../../../Services/organizations";
import { showSnackbarAction } from "../../../Redux/actions";
import { themeColors } from "../../theme";
import "../../../theme-vars.css";
import {Box,Grid,useTheme,useMediaQuery} from "@mui/material";
import InputTextValue from "../../TextInput/InputTextValue";
import { Circles } from "react-loader-spinner";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";

type Props = {
  open: boolean;                 // Whether modal is visible
  close: () => void;             // Close modal callback
  organizationId: any;           // ID of the current organization
  organizationUsers: any;        // Organization users (unused but mapped)
  theme?: boolean;               // Light/Dark theme flag
};

/**
 * NewsModal Component
 * -------------------------------------
 * Purpose:
 * Allows admin users to create a "News Link" entry for an area inside the organization.
 * Admin enters:
 *   - Area Name
 *   - External News Link
 *
 * Validations:
 *   ✔ Area name is required  
 *   ✔ News link is required
 *
 * Actions:
 *   - When submitted, triggers `createNews()` from organization services
 *   - Displays loader during API operation
 *   - Resets field values after success
 */
const NewsModal: FunctionComponent<Props> = ({
  organizationId,
  open,
  close,
  theme,
}) => {
    // Form fields
  const [area, setArea] = useState("");
  const [link, setLink] = useState("");

   // Loader state for submit button
  const [load, setLoad] = useState(false);
  const dispatcher = useDispatch();
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

   /**
   * Handle Save / Submit button click
   * Includes basic validation + API call to create news entry
   */
  const onSave = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!area.trim()) {
      dispatcher(showSnackbarAction("Please Enter Area Name!!", "error"));
      return;
    }
    if (!link.trim()) {
      dispatcher(showSnackbarAction("Please Enter News Link!!", "error"));
      return;
    }
    setLoad(true);
    
    // API call to create news item
    createNews(
      organizationId,
      area.trim(),
      link.trim(),
      dispatcher,
      (val) => setLoad(val),
      () => {
        close();
        setArea("");
        setLink("");
      }
    );
  };

    /**
   * Modal Styling (React Modal)
   * Uses theme-aware background + mobile responsive padding
   */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // darkens the background
      zIndex: 1300, // higher than MUI AppBar/Table
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={modalStyle}
      ariaHideApp={false}
    >
       {/* Loader overlay */}
      {load && <Loading />}
      <Box
        sx={{
          width: "100%",
          maxWidth: 340,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <form style={{ width: "100%" }}>
           {/* Modal Heading */}
          <Box sx={{ mb: 2 }}>
            <h3
              style={{
                color: theme ? "#272944" : "#fff",
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-large)",
                marginBottom: 24,
                textAlign: "center",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              Create News Link
            </h3>
          </Box>
           {/* Form Fields */}
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12}>
              <InputTextValue
                title="Area Name"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                required={true}
              />
            </Grid>
            <Grid item xs={12}>
              <InputTextValue
                title="News Link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                required={true}
              />
            </Grid>

             {/* Footer Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                  width: "100%",
                }}
              >
                {/* Cancel Button */}
               <Box style={{ marginRight:8 }}>
                 <StyledCancelButton onClick={close} isMobile={isMobile}>
                  Cancel
                </StyledCancelButton>
               </Box>
               
               {/* Submit Button */}
                <Box>
                  <StyledSubmitButton
                  onClick={onSave}
                  themeMode={themeMUI.palette.mode}
                  isMobile={isMobile}
                >
                  {load ? (
                    <span style={{ display: "flex", justifyContent: "center" }}>
                      <Circles color="#fff" height={20} width={20} />
                    </span>
                  ) : (
                    <>Submit</>
                  )}
                </StyledSubmitButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};

const mapStateToProps = (state: any) => {
  return {
    organizationId: state.organization.id,
    organizationUsers: state.organizationUsers.data,
    theme: state.theme.isLightMode,
  };
};

export default connect(mapStateToProps)(NewsModal);
